/**
 * MyPeerReviews — Employee peer review dashboard.
 *
 * Tab 1 "Feedback Given":
 *   • Lists all existing assignments where the user is the reviewer.
 *   • "Add Review" button opens a dialog to self-select a reviewee in any active cycle
 *     (open system — employees choose who to review, no hierarchy restriction).
 *   • Employee can cancel a pending assignment.
 *
 * Tab 2 "Feedback Received":
 *   • Aggregated, anonymous results for the current user as a reviewee.
 *   • Reviewer identity is never shown.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Tooltip,
  tooltipClasses,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import RateReviewIcon from "@mui/icons-material/RateReview";
import InfoIcon from "@mui/icons-material/Info";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { CustomTabPanel, a11yProps } from "../../../components/TabPanel/TabPanel";
import { fetchData, postData, deleteData } from "../../../store/actions";
import {
  myPeerReviewsUrl,
  myReceivedPeerReviewsUrl,
  peerReviewCyclesUrl,
  peerReviewAssignmentsUrl,
  availableRevieweesUrl,
} from "../../../Service/Apis";
import {
  AggregatedReviewResult,
  AggregatedTopicResult,
  PeerReviewAssignment,
  PeerReviewAssignmentStatus,
  PeerReviewCycle,
} from "./Types/PeerReviewTypes";
import { useCustomToast } from "../../../components/Methods/SnackBar";

// ── Styled tooltip ───────────────────────────────────────────────────────────

const ScoreTooltip = styled(({ className, ...props }: React.ComponentProps<typeof Tooltip>) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#1a1f2e",
    color: "#fff",
    borderRadius: 10,
    padding: "10px 14px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
    minWidth: 180,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#1a1f2e",
  },
}));

// ── Helpers ─────────────────────────────────────────────────────────────────

const statusColor = (
  s: PeerReviewAssignmentStatus
): "default" | "warning" | "success" | "error" => {
  if (s === "submitted") return "success";
  if (s === "expired") return "error";
  return "warning";
};

const daysUntil = (dateStr: string): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((new Date(dateStr).getTime() - today.getTime()) / 86_400_000);
};

interface AvailableReviewee {
  id: string;
  name: string;
  email: string;
  designation: string | null;
}

// ── Main component ───────────────────────────────────────────────────────────

const MyPeerReviews: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const showToast = useCustomToast();

  const [tabValue, setTabValue] = useState(0);

  // Cycle filter (shared across both tabs)
  const [cycleFilter, setCycleFilter] = useState<string>("all");
  const [allCycles, setAllCycles] = useState<PeerReviewCycle[]>([]);

  // Tab 1: Feedback Given
  const [assignments, setAssignments] = useState<PeerReviewAssignment[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(true);

  // Tab 2: Feedback Received
  const [receivedResults, setReceivedResults] = useState<AggregatedReviewResult[]>([]);
  const [receivedLoading, setReceivedLoading] = useState(false);

  // Available to review count (across all active cycles)
  const [availableToReviewCount, setAvailableToReviewCount] = useState<number | null>(null);

  // Add Review dialog
  const [addOpen, setAddOpen] = useState(false);
  const [activeCycles, setActiveCycles] = useState<PeerReviewCycle[]>([]);
  const [activeCyclesLoading, setActiveCyclesLoading] = useState(true);
  const [selectedCycle, setSelectedCycle] = useState<string>("");
  const [reviewees, setReviewees] = useState<AvailableReviewee[]>([]);
  const [revieweesLoading, setRevieweesLoading] = useState(false);
  const [selectedReviewee, setSelectedReviewee] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  const fetchAssignments = (cycle = cycleFilter) => {
    setAssignmentsLoading(true);
    const url =
      cycle !== "all"
        ? `${myPeerReviewsUrl}?cycle=${cycle}`
        : myPeerReviewsUrl;
    dispatch(fetchData(url))
      .then((res: any) => {
        if (res) setAssignments(res.results ?? res);
      })
      .finally(() => setAssignmentsLoading(false));
  };

  const fetchActiveCycles = () => {
    setActiveCyclesLoading(true);
    dispatch(fetchData(`${peerReviewCyclesUrl}?status=active&page_size=50`))
      .then((res: any) => {
        const cycles: PeerReviewCycle[] = res?.results ?? res ?? [];
        if (cycles.length) {
          setActiveCycles(cycles);
          // Fetch available reviewees for each active cycle and deduplicate
          Promise.all(
            cycles.map((c) =>
              dispatch(fetchData(availableRevieweesUrl(c.id))).then((r: any) =>
                (r?.employees ?? []) as AvailableReviewee[]
              )
            )
          ).then((allLists) => {
            const uniqueIds = new Set(allLists.flat().map((e) => e.id));
            setAvailableToReviewCount(uniqueIds.size);
          });
        }
      })
      .finally(() => setActiveCyclesLoading(false));
  };

  const fetchAllCycles = () => {
    dispatch(fetchData(`${peerReviewCyclesUrl}?page_size=100`)).then((res: any) => {
      if (res) setAllCycles(res.results ?? res);
    });
  };

  useEffect(() => {
    fetchAssignments();
    fetchActiveCycles();
    fetchAllCycles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReceivedResults = (cycle = cycleFilter) => {
    setReceivedLoading(true);
    const url =
      cycle !== "all"
        ? `${myReceivedPeerReviewsUrl}?cycle=${cycle}`
        : myReceivedPeerReviewsUrl;
    dispatch(fetchData(url))
      .then((res: any) => {
        if (res) setReceivedResults(Array.isArray(res) ? res : [res]);
      })
      .finally(() => setReceivedLoading(false));
  };

  const handleCycleFilterChange = (value: string) => {
    setCycleFilter(value);
    fetchAssignments(value);
    if (tabValue === 1) loadReceivedResults(value);
  };

  const handleTabChange = (_: React.SyntheticEvent, newVal: number) => {
    setTabValue(newVal);
    setEmployeeFilter("all");
    if (newVal === 1) loadReceivedResults(cycleFilter);
  };

  // ── Add Review dialog logic ────────────────────────────────────────────────

  const openAddDialog = () => {
    setAddOpen(true);
    setSelectedCycle("");
    setSelectedReviewee("");
    setReviewees([]);
  };

  const handleCycleChange = (cycleId: string) => {
    setSelectedCycle(cycleId);
    setSelectedReviewee("");
    setReviewees([]);
    if (!cycleId) return;
    setRevieweesLoading(true);
    dispatch(fetchData(availableRevieweesUrl(cycleId)))
      .then((res: any) => {
        if (res) setReviewees(res.employees ?? []);
      })
      .finally(() => setRevieweesLoading(false));
  };

  const handleAddReview = () => {
    if (!selectedCycle || !selectedReviewee) return;
    setSubmitting(true);
    dispatch(postData(peerReviewAssignmentsUrl, { cycle: selectedCycle, reviewee: selectedReviewee }))
      .then((res: any) => {
        if (res && !res.error) {
          showToast("Review added successfully!", "success", false);
          setAddOpen(false);
          fetchAssignments(cycleFilter);
        } else {
          const msg =
            res?.non_field_errors?.[0] ||
            res?.detail ||
            "Failed to add review.";
          showToast(msg, "error", false);
        }
      })
      .finally(() => setSubmitting(false));
  };

  const handleCancelAssignment = (id: string) => {
    dispatch(deleteData(`${peerReviewAssignmentsUrl}${id}/`))
      .then(() => {
        showToast("Assignment cancelled.", "success", false);
        fetchAssignments(cycleFilter);
      })
      .catch(() => showToast("Failed to cancel.", "error", false));
  };

  // ── Derived counts & employee filter ─────────────────────────────────────

  const [employeeFilter, setEmployeeFilter] = useState<string>("all");

  // Unique reviewees from the current assignments list (for employee dropdown)
  const revieweeOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: Array<{ id: string; name: string }> = [];
    assignments.forEach((a) => {
      const id = (a as any).reviewee as string;
      const name = (a as any).reviewee_name as string;
      if (id && !seen.has(id)) {
        seen.add(id);
        opts.push({ id, name: name || id });
      }
    });
    return opts;
  }, [assignments]);

  // Apply client-side employee filter for Tab 1
  const filteredAssignments =
    employeeFilter === "all"
      ? assignments
      : assignments.filter((a) => (a as any).reviewee === employeeFilter);

  const pending = filteredAssignments.filter((a) => a.status === "pending");
  const submitted = filteredAssignments.filter((a) => a.status === "submitted");

  return (
    <Container maxWidth="lg" sx={{ mt: 10, mb: 6 }}>
      <Typography variant="h5" fontWeight="bold" mb={2}>
        My Peer Reviews
      </Typography>

      {/* Summary chips + filters row */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="flex-start">
        <Chip icon={<PendingIcon />} label={`${pending.length} Pending`} color="warning" variant="outlined" />
        <Chip icon={<CheckCircleIcon />} label={`${submitted.length} Completed`} color="success" variant="outlined" />
        {availableToReviewCount !== null &&
          (cycleFilter === "all" || allCycles.find((c) => c.id === cycleFilter)?.status === "active") && (
          <Chip
            icon={<InfoIcon />}
            label={`${availableToReviewCount} Available to Review`}
            color="info"
            variant="outlined"
          />
        )}

        <Box flex={1} />

        {/* Cycle filter */}
        <Box display="flex" flexDirection="column" gap={0.5}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Cycle</InputLabel>
            <Select
              value={cycleFilter}
              label="Cycle"
              onChange={(e) => { handleCycleFilterChange(e.target.value); setEmployeeFilter("all"); }}
            >
              <MenuItem value="all">All Cycles</MenuItem>
              {allCycles.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1}>
                    <span>{c.name}</span>
                    <Typography
                      variant="caption"
                      sx={{
                        px: 0.8,
                        py: 0.2,
                        borderRadius: 1,
                        fontWeight: 600,
                        fontSize: "0.65rem",
                        textTransform: "uppercase",
                        backgroundColor:
                          c.status === "active" ? "#e8f5e9" :
                          c.status === "closed" ? "#ffebee" : "#fff8e1",
                        color:
                          c.status === "active" ? "#2e7d32" :
                          c.status === "closed" ? "#c62828" : "#f57f17",
                      }}
                    >
                      {c.status}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Selected cycle status hint */}
          {cycleFilter !== "all" && (() => {
            const sel = allCycles.find((c) => c.id === cycleFilter);
            if (!sel) return null;
            return (
              <Typography
                variant="caption"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: sel.status === "active" ? "success.main" : sel.status === "closed" ? "error.main" : "warning.main",
                  fontWeight: 500,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: sel.status === "active" ? "success.main" : sel.status === "closed" ? "error.main" : "warning.main",
                    display: "inline-block",
                  }}
                />
                Cycle is {sel.status === "active" ? "Open" : sel.status === "closed" ? "Closed" : "Draft"}
              </Typography>
            );
          })()}
        </Box>

        {/* Employee filter */}
        <Tooltip
          title={tabValue === 1 ? "Reviewer identity is anonymous — filter unavailable" : ""}
          placement="top"
        >
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={tabValue === 1}>
            <InputLabel>Employee</InputLabel>
            <Select
              value={tabValue === 1 ? "all" : employeeFilter}
              label="Employee"
              onChange={(e) => setEmployeeFilter(e.target.value)}
            >
              <MenuItem value="all">All Employees</MenuItem>
              {revieweeOptions.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Tooltip>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          textColor="inherit"
          indicatorColor="secondary"
          sx={{ "& .MuiTab-root": { color: "black" }, "& .Mui-selected": { color: "black" } }}
        >
          <Tab {...a11yProps(0)} label="Feedback Given" />
          <Tab {...a11yProps(1)} label="Feedback Received" />
        </Tabs>
      </Box>

      {/* ── Tab 1: Feedback Given ─────────────────────────────────────────────── */}
      <CustomTabPanel value={tabValue} index={0}>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Tooltip
            title={
              activeCyclesLoading
                ? "Checking active cycles…"
                : activeCycles.length === 0
                ? "No active cycles available"
                : ""
            }
          >
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAddDialog}
                disabled={activeCyclesLoading || activeCycles.length === 0}
              >
                Add Review
              </Button>
            </span>
          </Tooltip>
        </Box>

        {!activeCyclesLoading && activeCycles.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No active review cycles at the moment. HR will open a cycle before you can add reviews.
          </Alert>
        )}

        {assignmentsLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : filteredAssignments.length === 0 ? (
          <Alert severity="info">
            {employeeFilter !== "all"
              ? "No reviews found for the selected employee."
              : (() => {
                  const selectedCycleObj = cycleFilter !== "all"
                    ? allCycles.find((c) => c.id === cycleFilter)
                    : null;
                  if (selectedCycleObj && selectedCycleObj.status === "closed") {
                    return `No reviews were submitted in the "${selectedCycleObj.name}" cycle.`;
                  }
                  return "You haven't reviewed anyone yet. Once HR opens a cycle, use the \"Add Review\" button above to choose a colleague to review.";
                })()}
          </Alert>
        ) : (
          <Box>
            {/* Header row */}
            <Box
              display="grid"
              gridTemplateColumns="2fr 2fr 1.5fr 1fr 1.5fr"
              sx={{
                px: 2,
                py: 1,
                mb: 0.5,
                borderRadius: 2,
                backgroundColor: "#e8f5e9",
              }}
            >
              {["Employee", "Cycle", "Deadline", "Status", "Action"].map((h) => (
                <Typography key={h} variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {h}
                </Typography>
              ))}
            </Box>

            {/* Tile rows */}
            <Box display="flex" flexDirection="column" gap={1.5}>
              {filteredAssignments.map((a) => {
                const days = a.cycle_end_date ? daysUntil(a.cycle_end_date) : null;
                const isExpired = a.status === "expired";
                const isSubmitted = a.status === "submitted";
                const isPending = a.status === "pending";
                const isCycleClosed = a.cycle_status === "closed";
                const accentColor = isSubmitted
                  ? "#2e7d32"
                  : isExpired
                  ? "#c62828"
                  : days !== null && days <= 3
                  ? "#e65100"
                  : "#1565c0";

                return (
                  <Box
                    key={a.id}
                    display="grid"
                    gridTemplateColumns="2fr 2fr 1.5fr 1fr 1.5fr"
                    alignItems="center"
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                      backgroundColor: "#fff",
                      borderLeft: `4px solid ${accentColor}`,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                      transition: "box-shadow 0.2s, background-color 0.2s",
                      "&:hover": {
                        backgroundColor: "#f9fbe7",
                        boxShadow: "0 3px 10px rgba(0,0,0,0.12)",
                      },
                    }}
                  >
                    {/* Employee */}
                    <Typography variant="body2" fontWeight="medium" noWrap>
                      {(a as any).reviewee_name ?? "Colleague"}
                    </Typography>

                    {/* Cycle */}
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {(a as any).cycle_name ?? "—"}
                    </Typography>

                    {/* Deadline — hidden for closed cycles */}
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {isCycleClosed ? (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      ) : isExpired ? (
                        <Typography variant="body2" color="error">Expired</Typography>
                      ) : days !== null ? (
                        <>
                          <AccessTimeIcon sx={{ fontSize: 14, color: days <= 3 ? "error.main" : "text.secondary" }} />
                          <Typography
                            variant="body2"
                            color={days <= 0 ? "error" : days <= 3 ? "warning.main" : "text.secondary"}
                          >
                            {days <= 0 ? "Overdue" : days === 1 ? "Tomorrow" : `${days}d left`}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </Box>

                    {/* Status */}
                    <Chip label={a.status} color={statusColor(a.status)} size="small" sx={{ width: "fit-content" }} />

                    {/* Action */}
                    <Box display="flex" gap={1} alignItems="center">
                      {!isSubmitted && !isExpired && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<RateReviewIcon />}
                          onClick={() => navigate(`/pms/peer-review/my-reviews/${a.id}`)}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {isPending ? "Start" : "Continue"}
                        </Button>
                      )}
                      {isSubmitted && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigate(`/pms/peer-review/my-reviews/${a.id}`)}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          View
                        </Button>
                      )}
                      {isPending && (
                        <Tooltip title="Cancel this assignment">
                          <Button
                            variant="text"
                            size="small"
                            color="error"
                            onClick={() => handleCancelAssignment(a.id)}
                            sx={{ minWidth: "unset", p: 0.5 }}
                          >
                            <CancelIcon fontSize="small" />
                          </Button>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </CustomTabPanel>

      {/* ── Tab 2: Feedback Received ─────────────────────────────────────────── */}
      <CustomTabPanel value={tabValue} index={1}>
        {receivedLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (() => {
          // When no specific cycle is selected, only surface the active cycle(s).
          // The user can pick a closed cycle from the filter to view past feedback.
          const displayedResults =
            cycleFilter === "all"
              ? receivedResults.filter((r) => {
                  const cycle = allCycles.find((c) => c.id === r.cycle_id);
                  return cycle?.status === "active";
                })
              : receivedResults;

          if (displayedResults.length === 0) {
            return (
              <Alert severity="info">
                {cycleFilter === "all"
                  ? "No active cycle feedback yet. Use the Cycle filter above to view feedback from past cycles."
                  : "No reviews received yet. Results will appear here once peers submit their reviews."}
              </Alert>
            );
          }

          return displayedResults.map((result) => (
            <Paper key={result.cycle_id} elevation={2} sx={{ p: 3, mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6" fontWeight="bold">
                  {result.cycle_name}
                </Typography>
                {result.overall_avg_rating !== null && (
                  <Chip
                    label={`Overall: ${Number(result.overall_avg_rating).toFixed(2)}`}
                    color="primary"
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {result.submitted_reviews} of {result.total_reviewers} peers have reviewed you
              </Typography>

              <Divider sx={{ mb: 2 }} />

              {result.topics.map((topic: AggregatedTopicResult) => (
                <Box key={topic.topic_id} mb={3}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {topic.topic_name}
                    </Typography>
                    {topic.avg_rating !== null && (
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        Avg: {Number(topic.avg_rating).toFixed(2)}
                      </Typography>
                    )}
                  </Box>

                  {topic.questions.map((q) => {
                    const hasMinMax =
                      q.question_type !== "yes_no" &&
                      q.min_rating !== null &&
                      q.max_rating !== null;

                    const scale = 5;
                    const tooltipContent = hasMinMax ? (
                      <Box>
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ color: "rgba(255,255,255,0.55)", mb: 1, letterSpacing: 0.5, textTransform: "uppercase", fontSize: "0.65rem" }}
                        >
                          Score range
                        </Typography>

                        {/* Mini range bar */}
                        <Box sx={{ position: "relative", height: 6, borderRadius: 3, bgcolor: "rgba(255,255,255,0.12)", mb: 1.5, mx: 0.5 }}>
                          <Box
                            sx={{
                              position: "absolute",
                              left: `${(Number(q.min_rating) / scale) * 100}%`,
                              width: `${((Number(q.max_rating) - Number(q.min_rating)) / scale) * 100}%`,
                              height: "100%",
                              borderRadius: 3,
                              background: "linear-gradient(90deg, #f59e0b, #22c55e)",
                            }}
                          />
                        </Box>

                        <Box display="flex" justifyContent="space-between" gap={1.5}>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                              bgcolor: "rgba(239,68,68,0.18)",
                              border: "1px solid rgba(239,68,68,0.35)",
                              borderRadius: 1.5,
                              px: 1,
                              py: 0.4,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: "#fca5a5", fontWeight: 700, fontSize: "0.72rem" }}>
                              Min
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#fff", fontWeight: 700, fontSize: "0.78rem" }}>
                              {Number(q.min_rating).toFixed(1)}
                            </Typography>
                          </Box>

                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                              bgcolor: "rgba(34,197,94,0.18)",
                              border: "1px solid rgba(34,197,94,0.35)",
                              borderRadius: 1.5,
                              px: 1,
                              py: 0.4,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: "#86efac", fontWeight: 700, fontSize: "0.72rem" }}>
                              Max
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#fff", fontWeight: 700, fontSize: "0.78rem" }}>
                              {Number(q.max_rating).toFixed(1)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ) : "";

                    return (
                      <ScoreTooltip
                        key={q.question_id}
                        title={tooltipContent}
                        placement="top-start"
                        arrow
                        disableHoverListener={!hasMinMax}
                      >
                        <Box mb={1.5} pl={1} sx={{ cursor: hasMinMax ? "default" : "auto" }}>
                          <Typography variant="body2" mb={0.5}>
                            {q.question_text}
                          </Typography>
                          {q.question_type === "yes_no" ? (
                            <Box display="flex" gap={2}>
                              <Chip label={`Yes: ${q.yes_count ?? 0}`} color="success" size="small" variant="outlined" />
                              <Chip label={`No: ${q.no_count ?? 0}`} color="error" size="small" variant="outlined" />
                            </Box>
                          ) : q.avg_rating !== null ? (
                            <>
                              <Box display="flex" alignItems="center" gap={1}>
                                <LinearProgress
                                  variant="determinate"
                                  value={(Number(q.avg_rating) / 5) * 100}
                                  sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                                />
                                <Typography variant="caption" width={40}>
                                  {Number(q.avg_rating).toFixed(1)} / 5
                                </Typography>
                              </Box>
                              {hasMinMax && (
                                <Box display="flex" gap={2} mt={0.5}>
                                  <Typography variant="caption" color="text.secondary">
                                    Min: <strong>{Number(q.min_rating).toFixed(1)}</strong>
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Max: <strong>{Number(q.max_rating).toFixed(1)}</strong>
                                  </Typography>
                                </Box>
                              )}
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No ratings yet
                            </Typography>
                          )}
                          {q.comment_excerpts && q.comment_excerpts.length > 0 && (
                            <Box mt={0.5} pl={1}>
                              {q.comment_excerpts.map((c, ci) => (
                                <Typography
                                  key={ci}
                                  variant="caption"
                                  display="block"
                                  color="text.secondary"
                                  sx={{ fontStyle: "italic" }}
                                >
                                  "{c}"
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </ScoreTooltip>
                    );
                  })}
                </Box>
              ))}
            </Paper>
          ));
        })()}
      </CustomTabPanel>

      {/* ── Add Review Dialog ────────────────────────────────────────────────── */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Review</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Select an active cycle and choose a colleague to review. You cannot review yourself or
            someone you have already reviewed in the same cycle.
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Cycle</InputLabel>
            <Select
              value={selectedCycle}
              label="Cycle"
              onChange={(e) => handleCycleChange(e.target.value)}
            >
              {activeCycles.length === 0 ? (
                <MenuItem disabled>No active cycles</MenuItem>
              ) : (
                activeCycles.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                    {c.end_date && (
                      <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                        (ends {c.end_date})
                      </Typography>
                    )}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {selectedCycle && (
            <>
              {revieweesLoading ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : reviewees.length === 0 ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  No available colleagues to review in this cycle.
                </Alert>
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel>Select colleague to review</InputLabel>
                  <Select
                    value={selectedReviewee}
                    label="Select colleague to review"
                    onChange={(e) => setSelectedReviewee(e.target.value)}
                  >
                    {reviewees.map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.name}
                        {r.designation && (
                          <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                            · {r.designation}
                          </Typography>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddReview}
            disabled={!selectedCycle || !selectedReviewee || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : "Add Review"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyPeerReviews;
